import { useGetBrIefById } from "@entities/brief/lib/hooks/useGetBriefById";
import { useGetFinanceInfluencer } from "@entities/campaigns/lib/hooks/useGetFInanceInfluencer";
import { Flex, Modal, Row, Spin } from "antd";

import { useUpdateInfluencer } from "@entities/influencer";
import { useGetInfluencerFacets } from "@entities/influencer/lib/hooks/useGetFacets";
import { useGetInfluencer } from "@entities/influencer/lib/hooks/useGetInfluencerById";
import { InfluencerCard } from "@entities/influencer/ui/InfluencerCard";
import InfluencerUpdateForm from "@entities/influencer/ui/InfluencerUpdateForm";
import { useBriefStore } from "@pages/layout/BriefLayout/lib/useLayoutStore";
import { IInfluencer } from "@shared/lib/types/influencer";
import { Button } from "@shared/ui/Button";
import { useEffect, useState } from "react";
import { BillingDetails } from "./BillingDetails";
import { BillingEmptyState } from "./BillingEmptyState";
import { GstPayout } from "./GstPayout";
import { InvoiceDetails } from "./InvoiceDetails";
import { LedgerDetails } from "./LedgerDetails";
import { Payment } from "./Payment";
import { Payouts } from "./Payouts";
import styles from "./styles.module.scss";
import { TotalDetails } from "./TotalDetails";

interface Props {
  campaignInfluencerId: string;
  onClose: () => void;
}

export function VendorPayment({ campaignInfluencerId, onClose }: Props) {
  const {
    financeInfluencer,
    isError,
    isLoading,
    refetchFinanceData,
    isRefetching,
  } = useGetFinanceInfluencer(campaignInfluencerId);

  const briefId = financeInfluencer?.campaignInfluencer?.briefId || "";
  const { brief: storeBrief } = useBriefStore();
  const { brief: fetchedBrief } = useGetBrIefById(
    storeBrief?._id === briefId ? "" : briefId
  );
  const campaignName =
    (storeBrief?._id === briefId ? storeBrief?.name : undefined) ||
    fetchedBrief?.name ||
    "";

  const [isInfluencerModalOpen, setInfluencerModalOpen] = useState(false);
  const [isGSTPayoutModalOpen, setGSTPayoutModalOpen] = useState(false);
  const { update, isUpdating, isUpdated } = useUpdateInfluencer();
  const { facets } = useGetInfluencerFacets();
  const categories = (facets?.category || [])?.map((item) => item._id) || [];
  const [isPayoutModalProps, setPayoutModalProps] = useState<{
    visible: boolean;
    type: "full" | "partial" | "manual";
  }>({
    visible: false,
    type: "full",
  });

  const {
    influencer,
    isFetching: isFetchingInfluencer,
    refetch,
  } = useGetInfluencer(
    financeInfluencer?.campaignInfluencer?.influencerId || ""
  );

  const handleSubmit = (influencerData: Partial<IInfluencer>) => {
    update({
      influencerId: financeInfluencer?.campaignInfluencer?.influencerId || "",
      influencer: influencerData,
    });
    setInfluencerModalOpen(false);
  };

  useEffect(() => {
    if (isUpdated) {
      refetch();
    }
  }, [isUpdated]);

  if (isLoading || isFetchingInfluencer) {
    return (
      <Row justify="center" align="middle" style={{ height: "100%" }}>
        <Spin />
      </Row>
    );
  }

  if (isError) {
    return <div />;
  }

  return (
    <>
      {" "}
      <div className={styles.container}>
        {" "}
        <InfluencerCard
          isOnboarded
          handleEditClick={() => {}}
          influencer={influencer}
          creatorInfo={{
            image:
              financeInfluencer?.campaignInfluencer?.socialMedia?.profilePicUrl,
            name: influencer?.firstName + " " + influencer?.lastName,
            username:
              financeInfluencer?.campaignInfluencer?.socialMedia?.username,
          }}
          isV2={true}
        />
        {!financeInfluencer?.campaignInfluencer?.isVendorBillingAttached ? (
          <BillingEmptyState
            campaignInfluencer={financeInfluencer?.campaignInfluencer}
          />
        ) : (
          <>
            {financeInfluencer?.billingEntity && (
              <BillingDetails financeInfluencer={financeInfluencer} />
            )}
            {financeInfluencer?.aggregates && (
              <TotalDetails aggregate={financeInfluencer?.aggregates} />
            )}
            <LedgerDetails
              ledgers={financeInfluencer?.ledgers || []}
              campaignInfluencerId={campaignInfluencerId}
              billing={financeInfluencer?.billingEntity}
            />
            <InvoiceDetails
              invoices={financeInfluencer?.invoices || []}
              campaignInfluencerId={campaignInfluencerId}
              billing={financeInfluencer?.billingEntity}
            />
            <Payouts
              invoices={financeInfluencer?.invoices || []}
              payments={financeInfluencer?.payments || []}
              campaignInfluencerId={campaignInfluencerId}
              refetch={refetchFinanceData}
              isRefetching={isRefetching}
            />
          </>
        )}
      </div>
      {financeInfluencer?.campaignInfluencer?.isVendorBillingAttached && (
        <Flex justify="end" gap={8} className={styles.stickyButtonGroup}>
          <Button onClick={() => setGSTPayoutModalOpen(true)}>
            Payout GST
          </Button>
          <Button
            onClick={() =>
              setPayoutModalProps({ visible: true, type: "manual" })
            }
          >
            Create Manual Payout
          </Button>
          <Button
            onClick={() =>
              setPayoutModalProps({ visible: true, type: "partial" })
            }
          >
            Create Partial Payout
          </Button>
          <Button
            type="primary"
            onClick={() => setPayoutModalProps({ visible: true, type: "full" })}
          >
            Create Payout ₹ (
            {(financeInfluencer?.aggregates?.payments?.base?.left || 0).toFixed(
              2
            )}
            )
          </Button>
        </Flex>
      )}
      <Modal
        title="Update Influencer"
        open={isInfluencerModalOpen}
        onCancel={() => setInfluencerModalOpen(false)}
        footer={null}
        destroyOnClose
      >
        <InfluencerUpdateForm
          initialValues={influencer}
          isSubmitting={isUpdating}
          onSubmit={handleSubmit}
          initialCategories={categories}
        />
      </Modal>
      <Modal
        title="Create Payout"
        open={isPayoutModalProps.visible}
        onCancel={() => setPayoutModalProps({ visible: false, type: "full" })}
        footer={null}
        destroyOnClose
        width={800}
      >
        <Payment
          invoices={financeInfluencer?.invoices || []}
          campaignInfluencerId={campaignInfluencerId}
          fullAmount={{
            base: financeInfluencer?.aggregates?.payments?.base?.left || 0,
            gst: financeInfluencer?.aggregates?.payments?.gst?.left || 0,
          }}
          type={isPayoutModalProps.type}
          onClose={() => setPayoutModalProps({ visible: false, type: "full" })}
          primaryContactText={
            financeInfluencer?.billingEntity?.emailId ||
            `${financeInfluencer?.billingEntity?.countryCode} ${financeInfluencer?.billingEntity?.phoneNumber}` ||
            ""
          }
          isPrimaryEmail={!!financeInfluencer?.billingEntity?.emailId}
          campaignName={campaignName}
        />
      </Modal>
      <Modal
        title="Create GST Payout"
        open={isGSTPayoutModalOpen}
        onCancel={() => setGSTPayoutModalOpen(false)}
        footer={null}
        destroyOnClose
      >
        <GstPayout
          campaignInfluencerId={campaignInfluencerId}
          maxAmount={financeInfluencer?.aggregates?.payments?.gst?.left || 0}
          invoices={financeInfluencer?.invoices || []}
          onClose={() => setGSTPayoutModalOpen(false)}
          primaryContactText={
            financeInfluencer?.billingEntity?.emailId ||
            `${financeInfluencer?.billingEntity?.countryCode} ${financeInfluencer?.billingEntity?.phoneNumber}` ||
            ""
          }
          isPrimaryEmail={!!financeInfluencer?.billingEntity?.emailId}
        />
      </Modal>
    </>
  );
}
